import Axios from 'axios';
import MapController from './MapController';

const URL_BASE = 'https://api3.private-radar.com/openaip';
const KEYWORD_FRAME = '{FRAME}';
const KEYWORD_ZOOM = '{ZOOM}';
const URL_GET_AIRPORTS = `${URL_BASE}/Airport/GetAirports?frame=${KEYWORD_FRAME}&zoom=${KEYWORD_ZOOM}`;

const AirportType = { AF_MIL_CIVIL:0, GLIDING:1, AF_CIVIL:2, INTL_APT:3, UNKNOWN:4, AD_MIL:5, LIGHT_AIRCRAFT:6, HELI_CIVIL:7, AD_CLOSED:8, AF_IFR:9, AF_WATER:10 };


export default class AirportLayer {

    constructor(map) {
        this.map = map;
        this.airports = []; // airports from server
        this.markers = [];
        this.showPath = false;
        this.timestamp = 0;
        this.spriteImg = "./img/airports.png";
        this.spriteSize = 28;
        this.label1Value = "icao";
        this.label2Value = ""; // alt
        this.label3Value = "twr";
        this.labelMinZoom = 8;
        this.showLayer = true;
        this.showLabels = true;
        this.showTooltip = false;
        this.showRunwayTooltip = false;
        this.showFrequencyTooltip = false;
        this.MarkerWithLabel = require ('@google/markerwithlabel');//Must import npm packages here

        this.init();
    }

    init() {
        this.getSettingsFromLocalStorage();
        // We catch idle to start loading the airports
        const me = this;
        window.google.maps.event.addListener(this.map, 'idle', function () {

            me.mapIdle();
        });
    }

    mapIdle() {
        this.eventListener();
        
        // The map is idle = finished to load => load airports
        this.loadAirports();
    }

    // Get settings from LS so we initialize the map with the last settings applied
    getSettingsFromLocalStorage() {
        // Get settings from localStorage
        let settings = JSON.parse(localStorage.getItem('mapsettings'));
        // If mapopts is not null
        if (settings !== null) {
            // Set settings
            this.showLayer = settings.showAirports;
        }
    }

    // Load Airports
    async loadAirports () {
        // If we dont display the layer stop here
        if (!this.showLayer)
            return;

        // Get
        let frame = MapController.getFrameTxt(this.map);
        let zoom = this.map.getZoom();
        let url = URL_GET_AIRPORTS.replace(KEYWORD_FRAME, frame).replace(KEYWORD_ZOOM, zoom);
		const res = await Axios.get(url);

        // Error
        if (res.status !== 200) {
            // Deal with the error
            console.error('loadAirports error ' + res.error);
            return;
        }

        // Success
        console.log(`Loaded ${res.data.length} airports`, res.data);
        this.addAirports(res.data);
    }

    eventListener() {
        window.addEventListener('newMapOpts', (e) => {
            this.showLayer = e.detail.showAirports;
            if (!e.detail.showAirports) {
                this.markers.map((a, i) => {
                    a.marker.setMap(null);
                    this.markers.splice(i, 1);
                    return null;
                });
            };
            if (e.detail.showAirports)
                return this.loadAirports();
        })
    }

    showLabel(l) {
        this.markers.map((a, i) => {
            
            // If type of label is set to none
            if (!this.showLabels)
                return a.marker.labelVisible = false;
            
            // If other type of label get label and set it
            a.marker.labelContent = a.marker.airport.icao
            a.marker.labelVisible = true;
            a.marker.setVisible(true);
            return null;
        })
    }

    addAirports(airports) {
        // Add airports to this.airports
        this.airports = airports;

        // Update markers on the map
        this.updateMarkers();
    }

    // Markers
    updateMarkers() {
        for (const airport of this.airports) {
            // If there is already a marker do nothing o marker isn't on this.airports
            const markerIndex = this.markers.findIndex(a => a.id === airport.id);
            if (markerIndex === -1)
                this.addMarker(airport);
        }
        this.removeMarkersNotInAirports();
    }

    addMarker(airport) {
        let sprite = './img/airports.png';
        let size = this.spriteSize;

        let spritePos = this.getAirportSpritePos(airport, size);
        const { _id, lat, lng } = airport;
        let title =  this.getAirportTitle(airport);
        
        // Marker
        let marker = new this.MarkerWithLabel({
            map: this.map,
            position: { lat, lng },
            visible: true,
            // z-index less then all aircrafts and labels
            zIndex:96,
            draggable: false,
            raiseOnDrag: true,
            labelContent: airport.icaoCode,
            labelClass: "airport_label", // the CSS class for the label
            labelAnchor: new window.google.maps.Point(15, -15),
            labelVisible: this.showLabels,
            icon: new window.google.maps.MarkerImage(sprite, new window.google.maps.Size(size, size), spritePos, new window.google.maps.Point(size / 2, size / 2)),
            title,
            airport
        });

        // Click event
        marker.addListener('click', (e) => {
            let event = new CustomEvent('airportClick', {
                detail: airport._id
            });
            window.dispatchEvent(event);
        })
        
        // Add to markers
        this.markers.push({
            id : _id,
            marker
        });
    }

    getAirportTitle(airport) {
        let title = airport.name;
        if (airport.icaoCode !== '')
            title += ` (${airport.icaoCode})`;
        return title;
    }

    getAirportSpritePos(airport, size) {
        let x = 0;
        let y = 0;
        // Rotate the sprite
        switch (airport.type) {
            case AirportType.HELI_CIVIL: 
                x = 1;
                break;
            default:
                if (airport.icaoCode !== '' && airport.runways.length >= 0)
                    x = this.getMainRunwayHeading(airport) + 1;
                break;
        }
        return new window.google.maps.Point(x * size, y * size)
    }

    getMainRunwayHeading(airport) {
        if (!airport || !airport.runways || airport.runways.length === 0)
            return 0;

        // Get main runways
        let runway = airport.runways.find(x => x.mainRunway);

        // No main runway => use first one
        if (runway === null)
            runway = airport.runways[0];

        // We need to find the heading of the runway between 10 and 180 degrees. If it's more than 180, remove 180
        let heading = runway.trueHeading;
        if (heading > 180)
            heading -= 180;

        // Return heading
        return Math.round(heading / 10);
    }

    removeMarkersNotInAirports() {
        for (let i = 0; i < this.markers.length; i++) {
            let m = this.markers[i];
            if (m == null)
                continue;

            if (!this.IsInAirports(m.id)) {
                m.marker.setMap(null);
                this.markers.splice(i, 1);
                i--; // important! we just removed one in the array...
            } else {
                //console.log("Keep " + m.id);
            }
        }
    }

    IsInAirports(id) {
        return this.airports.find(x => x.id === id) !== null;
    }
}